import React, { useState } from 'react';
import { Box, Dialog, IconButton, TextField, Tooltip } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CommonText from '../../../form-components/CommonText';
import { FormInputDate } from '../../../form-components/FormInputDate';
import { FormInputDropdown } from '../../../form-components/FormInputDropdown';
import MemberMemoSection from './MemberMemoSection';
import MemberGroupChips from '../../member/modal/memberGroup/MemberGroupChips';
import {
  AvatarImage,
  OutlinedRoundButton,
  SectionTitle,
  ProfileSectionRow,
  ProfileSection,
  ConsultationSection,
  PointRow,
  PointLabel,
  PointValue,
  PointChangeButton,
} from './UserGeneralTabStyled';

// 회원 상세 첫 탭의 상단 — [아바타 + 소속/등록일/담당자 + 보유 포인트] 와 [상담 내역]을 별개 섹션으로 분리.
// (메모는 페이지 헤더로 이동 — UserDetailUserInfo / MemoEditModal)
const UserProfileSection = ({
  t,
  user,
  bigUser,
  control,
  previewImage,
  canInfoEdit,
  userInfo,
  userBranchId,
  userTrainerId,
  onPointChange,
  onTrainerChange,
  onOpenDefaultModal,
  onOpenGuardianModal,
  onOpenPointModal,
  onMoreConsultations,
}) => {
  const [isPhotoOpen, setIsPhotoOpen] = useState(false);

  const avatarSrc = previewImage
    ? typeof previewImage === 'string'
      ? previewImage.startsWith('data:')
        ? previewImage
        : `data:image/png;base64,${previewImage}`
      : (window.URL || window.webkitURL).createObjectURL(previewImage)
    : (user.profileImage ?? '/images/profile@2x.png');

  // 기본 플레이스홀더 이미지는 확대할 의미가 없으므로 실제 사진일 때만 클릭 확대.
  const hasPhoto = Boolean(previewImage || user.profileImage);

  return (
    <Box sx={{ mb: 4, width: '100%' }}>
      <ProfileSectionRow>
        {/* Section 1: 프로필 (아바타 + 소속/등록일/담당자 + 보유 포인트) */}
        <ProfileSection>
          {/* 아바타 + 정보 가로 배치 (보유 포인트는 아래 카드 full-width) */}
          <Box sx={{ display: 'flex', gap: '24px', width: '100%' }}>
            {/* 아바타 + 액션 버튼 */}
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '8px',
                flexShrink: 0,
              }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <label htmlFor="imageupload">
                <Box position="relative">
                  <AvatarImage
                    src={avatarSrc}
                    alt="avatar"
                    onClick={hasPhoto ? () => setIsPhotoOpen(true) : undefined}
                    style={hasPhoto ? { cursor: 'zoom-in' } : undefined}
                  />
                </Box>
              </label>
            </Box>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
              }}>
              {previewImage && (
                <CommonText variant="hint" sx={{ textAlign: 'center', fontWeight: 500 }}>
                  {t('안면등록완료')}
                </CommonText>
              )}
              <Tooltip title={!canInfoEdit ? '권한이 없습니다' : ''} arrow>
                <OutlinedRoundButton
                  variant="contained"
                  onClick={canInfoEdit ? onOpenDefaultModal : undefined}
                  style={!canInfoEdit ? { opacity: 0.5, cursor: 'default' } : {}}>
                  {t('상세보기')}
                </OutlinedRoundButton>
              </Tooltip>
              <OutlinedRoundButton variant="contained" onClick={onOpenGuardianModal}>
                {t('보호자등록')}
              </OutlinedRoundButton>
            </Box>
          </Box>

          {/* 소속 / 등록일 / 담당자 */}
          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              gap: '12px',
            }}>
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              <SectionTitle variant="label">{t('Studio')}</SectionTitle>
              <FormInputDropdown
                name="pointID"
                disabled={!user?.memberBranchList || user.memberBranchList.length === 0}
                control={control}
                onChange={onPointChange}
                staticValue={userBranchId}
                options={(user?.memberBranchList || []).map(p => ({
                  value: p.branchId,
                  label: p.branchName,
                }))}
              />
            </Box>
            <Box className="form1" sx={{ display: 'flex', flexDirection: 'column' }}>
              <SectionTitle variant="label">{t('Registration date​​')}</SectionTitle>
              {/* 이 지점에 처음 등록한 날(백로그 #170). 수정은 상세보기에서 한다 */}
              <FormInputDate
                name="branchRegisteredDate"
                color="info"
                disabled
                control={control}
                placeholder={t('DD/MM/YYYY')}
                size="small"
                variant="outlined"
                renderInput={params => (
                  <TextField
                    size="small"
                    variant="outlined"
                    {...params}
                    sx={{
                      background: '#fff',
                      '& .css-i4bv87-MuiSvgIcon-root': {
                        fill: '#0F7F85 !important',
                        display: 'none',
                      },
                    }}
                  />
                )}
              />
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              <SectionTitle variant="label" sx={{ display: 'flex', alignItems: 'center' }}>
                {t('Management')}
                &nbsp;
              </SectionTitle>
              <FormInputDropdown
                name="trainerId"
                staticValue={userTrainerId}
                onChange={onTrainerChange}
                control={control}
                options={(userInfo?.trainers || []).map(p => ({
                  value: p.trainerId,
                  label: p.trainerName,
                }))}
              />
            </Box>

          </Box>
          </Box>
          {/* 회원 그룹 — 지점 안에서 이 회원이 속한 그룹을 보고 바로 넣고 뺀다 */}
          <MemberGroupChips
            memberId={user?.memberId}
            memberName={user?.fullname || user?.name}
            branchId={userBranchId}
          />
          <PointRow>
            <PointLabel>{t('보유 포인트')}</PointLabel>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <PointValue>{Number(userInfo?.holdingPoint || 0).toLocaleString()} P</PointValue>
              <PointChangeButton onClick={onOpenPointModal}>{t('변경')}</PointChangeButton>
            </Box>
          </PointRow>
        </ProfileSection>

        {/* Section 2: 메모 */}
        <ConsultationSection>
          <MemberMemoSection t={t} user={user} bigUser={bigUser} branchId={userBranchId} />
        </ConsultationSection>
      </ProfileSectionRow>

      {/* 프로필/안면등록 사진 확대 보기 */}
      <Dialog
        open={isPhotoOpen}
        onClose={() => setIsPhotoOpen(false)}
        maxWidth={false}
        PaperProps={{ sx: { m: 0, maxWidth: 'none', maxHeight: 'none', overflow: 'hidden' } }}>
        <Box sx={{ position: 'relative', display: 'flex', lineHeight: 0 }}>
          <IconButton
            onClick={() => setIsPhotoOpen(false)}
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              color: '#FFFFFF',
              backgroundColor: 'rgba(0, 0, 0, 0.45)',
              '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.65)' },
            }}>
            <CloseIcon />
          </IconButton>
          <img
            src={avatarSrc}
            alt="avatar"
            onClick={() => setIsPhotoOpen(false)}
            style={{ height: '90vh', maxWidth: '95vw', objectFit: 'contain', cursor: 'zoom-out' }}
          />
        </Box>
      </Dialog>
    </Box>
  );
};

export default UserProfileSection;
